/**
 * useGridGrouping — the default open/closed answer for one group or tree node.
 *
 *   ┌ per node ──────────────────────────────────────────────────────┐
 *   │  expandedGroups[groupingKey]   the user's own last click,      │
 *   │        │                       held in the grid-core store     │
 *   │        ▼  present? use it                                      │
 *   │  ── absent ──► depth rule:  -1 ⇒ open every level              │
 *   │                             n ⇒ open while depth < n           │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * The store is SHARED by every consumer under one `GridCoreProvider`:
 * `ExpandMore` and `ExpandAllButton` write `expandedGroups`, this hook reads it.
 * That is why a nested provider is deliberately avoided by the accounting
 * report grid (`AccountingReportGrid.tsx:68`).
 *
 * Body moved VERBATIM from `DataGrid.tsx`'s `PersistentDataGrid`. Two things
 * that read as changes are not: the node parameter was implicitly `any` and is
 * now MUI's own `GridGroupNode`, and the key is normalised with `String(...)`
 * because `groupingKey` is `string | number | boolean | null` while the store is
 * keyed by `GridRowId` — a JS property lookup already stringifies every one of
 * those, so the resolved property is identical.
 */
import type { DataGridPremiumProps, GridGroupNode } from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { useGridCore } from 'src/providers/GridCoreProvider';

export type GridGroupingOptions = {
  /**
   * MUI's own prop, read here rather than forwarded: `-1` opens every level,
   * `n` opens while `depth < n`, absent leaves every untouched node closed.
   */
  defaultGroupingExpansionDepth?: number;
};

/**
 * The single MUI prop this capability owns. Destructured explicitly at the call
 * site — see `grouping/index.ts` for the disjoint-props declaration.
 */
export type GridGroupingProps = {
  isGroupExpandedByDefault: NonNullable<DataGridPremiumProps['isGroupExpandedByDefault']>;
};

export function useGridGrouping({ defaultGroupingExpansionDepth }: GridGroupingOptions): GridGroupingProps {
  const expandedGroups = useGridCore((store) => store.expandedGroups);

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) =>
      expandedGroups[String(node.groupingKey)] ??
      (defaultGroupingExpansionDepth === -1 ||
        (defaultGroupingExpansionDepth !== undefined && node.depth < defaultGroupingExpansionDepth)),
    [defaultGroupingExpansionDepth, expandedGroups]
  );

  return useMemo(() => ({ isGroupExpandedByDefault }), [isGroupExpandedByDefault]);
}
